import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import {
  Activity, RefreshCw, CheckCircle, XCircle, Server, AlertTriangle,
  Loader2, Search, Clock, Play, Pause, Heart, ShieldOff, RotateCcw,
  Ban, ArrowUpDown, ChevronLeft, ChevronRight, Radio, Wifi, WifiOff,
  ArrowLeft, Eye, Timer, TrendingUp, Zap, Bell, Mail, MessageSquare,
  Settings, X, Plus, Trash2, Save
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

const STATUS_CONFIG = {
  healthy:           { label: 'Healthy',      color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  failing:           { label: 'Failing',       color: 'bg-amber-100 text-amber-700 border-amber-200',     dot: 'bg-amber-500 animate-pulse' },
  auto_excluded:     { label: 'Auto-Excluded', color: 'bg-red-100 text-red-700 border-red-200',           dot: 'bg-red-500' },
  manually_excluded: { label: 'Excluded',      color: 'bg-slate-100 text-slate-600 border-slate-200',     dot: 'bg-slate-400' },
  never_synced:      { label: 'Never Synced',  color: 'bg-blue-100 text-blue-700 border-blue-200',        dot: 'bg-blue-500' },
  unknown:           { label: 'Unknown',       color: 'bg-gray-100 text-gray-600 border-gray-200',        dot: 'bg-gray-400' },
};

function formatTimeAgo(seconds) {
  if (seconds == null) return 'Never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatDuration(seconds) {
  if (seconds == null || seconds === 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function SyncPerformanceCard({ history, stats }) {
  const maxDuration = Math.max(...history.map(h => h.duration_seconds || 0), 1);

  return (
    <Card data-testid="sync-performance-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
            <Timer className="w-5 h-5 text-[#1a5490]" />
            Sync Performance
          </CardTitle>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-emerald-500" /> Avg: <strong className="text-gray-700">{formatDuration(stats.avg_duration_seconds)}</strong></span>
            <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-blue-500" /> Fastest: <strong className="text-gray-700">{formatDuration(stats.fastest_seconds)}</strong></span>
            <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-500" /> Slowest: <strong className="text-gray-700">{formatDuration(stats.slowest_seconds)}</strong></span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Duration bar chart */}
        <div className="flex items-end gap-[3px] h-24 mb-4" data-testid="sync-duration-chart">
          {[...history].reverse().map((entry, i) => {
            const d = entry.duration_seconds || 0;
            const pct = Math.max(4, (d / maxDuration) * 100);
            const isSlow = entry.is_slow;
            const isFailed = (entry.assets_failed || 0) > 0 && entry.status?.startsWith('Error');
            const barColor = isFailed ? 'bg-red-400' : isSlow ? 'bg-amber-400' : 'bg-emerald-400';
            return (
              <div
                key={entry.id || i}
                className="flex-1 group relative"
                data-testid={`sync-bar-${i}`}
              >
                <div
                  className={`${barColor} rounded-t-sm transition-all hover:opacity-80 cursor-pointer min-h-[4px]`}
                  style={{ height: `${pct}%` }}
                />
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-20 pointer-events-none">
                  <div className="bg-gray-900 text-white text-[10px] rounded-md px-2.5 py-1.5 whitespace-nowrap shadow-lg">
                    <div className="font-medium">{formatDuration(d)}</div>
                    <div className="text-gray-300">{entry.assets_synced} synced, {entry.assets_failed} failed</div>
                    <div className="text-gray-400">{new Date(entry.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                    {isSlow && <div className="text-amber-300 font-medium mt-0.5">Slow cycle (2x+ avg)</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-[11px] text-gray-500 mb-3">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" /> Normal</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400" /> Slow (2x+ avg)</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-400" /> Error</span>
          <span className="ml-auto">Last {history.length} cycles (newest right)</span>
        </div>

        {/* Recent cycles table */}
        <div className="max-h-48 overflow-y-auto border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-xs py-1.5">Date</TableHead>
                <TableHead className="text-xs py-1.5">Duration</TableHead>
                <TableHead className="text-xs py-1.5">Synced</TableHead>
                <TableHead className="text-xs py-1.5">Failed</TableHead>
                <TableHead className="text-xs py-1.5">Triggered By</TableHead>
                <TableHead className="text-xs py-1.5">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((entry, i) => {
                const isSlow = entry.is_slow;
                const isFailed = entry.status?.startsWith('Error');
                return (
                  <TableRow key={entry.id || i} className={isSlow ? 'bg-amber-50/50' : isFailed ? 'bg-red-50/50' : ''} data-testid={`sync-history-row-${i}`}>
                    <TableCell className="text-xs py-1.5 text-gray-600">
                      {new Date(entry.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </TableCell>
                    <TableCell className="py-1.5">
                      <span className={`text-xs font-medium ${isSlow ? 'text-amber-700' : isFailed ? 'text-red-700' : 'text-gray-900'}`}>
                        {formatDuration(entry.duration_seconds)}
                      </span>
                      {isSlow && <AlertTriangle className="w-3 h-3 text-amber-500 inline ml-1" />}
                    </TableCell>
                    <TableCell className="text-xs py-1.5 text-emerald-700 font-medium">{entry.assets_synced}</TableCell>
                    <TableCell className="text-xs py-1.5">
                      {entry.assets_failed > 0 ? (
                        <span className="text-red-600 font-medium">{entry.assets_failed}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs py-1.5 text-gray-500">{entry.triggered_by || '—'}</TableCell>
                    <TableCell className="text-xs py-1.5">
                      <Badge variant="outline" className={`text-[10px] py-0 ${
                        isFailed ? 'border-red-300 text-red-700 bg-red-50' :
                        isSlow ? 'border-amber-300 text-amber-700 bg-amber-50' :
                        'border-emerald-300 text-emerald-700 bg-emerald-50'
                      }`}>
                        {isFailed ? 'Error' : isSlow ? 'Slow' : 'OK'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function SyncAlertSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    api.get('/settings/sync-alerts').then(r => { setConfig(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const saveConfig = async (updates) => {
    setSaving(true);
    try {
      await api.put('/settings/sync-alerts', updates);
      setConfig(prev => ({ ...prev, ...updates }));
      toast.success('Alert settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    const list = [...(config?.email_recipients || [])];
    if (list.includes(email)) { toast.error('Already added'); return; }
    list.push(email);
    saveConfig({ email_recipients: list });
    setNewEmail('');
  };

  const removeEmail = (email) => {
    const list = (config?.email_recipients || []).filter(e => e !== email);
    saveConfig({ email_recipients: list });
  };

  const sendTest = async (channel) => {
    setTesting(channel);
    try {
      const payload = channel === 'email' ? { test_email: true } : { test_slack: true };
      const res = await api.post('/settings/sync-alerts/test', payload);
      const result = res.data[channel];
      if (result === 'sent') toast.success(`Test ${channel} sent!`);
      else toast.error(`Test ${channel}: ${result}`);
    } catch { toast.error(`Failed to send test ${channel}`); }
    finally { setTesting(null); }
  };

  if (loading || !config) return null;

  return (
    <Card data-testid="sync-alert-settings">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setIsOpen(o => !o)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-gray-900 flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#1a5490]" />
            Slow Sync Alerts
          </CardTitle>
          <div className="flex items-center gap-2">
            {(config.email_enabled || config.slack_enabled) && (
              <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">Active</Badge>
            )}
            <Settings className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
          </div>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="pt-0 space-y-4">
          <p className="text-xs text-gray-500">Get notified when a sync cycle takes significantly longer than average.</p>

          {/* Threshold + Cooldown */}
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Alert threshold:</label>
              <select
                value={config.threshold_multiplier || 3}
                onChange={(e) => saveConfig({ threshold_multiplier: Number(e.target.value) })}
                className="text-xs border rounded px-2 py-1 bg-white"
                data-testid="alert-threshold-select"
              >
                <option value={2}>2x average</option>
                <option value={3}>3x average (recommended)</option>
                <option value={4}>4x average</option>
                <option value={5}>5x average</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Cooldown:</label>
              <select
                value={config.cooldown_minutes || 60}
                onChange={(e) => saveConfig({ cooldown_minutes: Number(e.target.value) })}
                className="text-xs border rounded px-2 py-1 bg-white"
                data-testid="alert-cooldown-select"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>1 hour (recommended)</option>
                <option value={120}>2 hours</option>
                <option value={360}>6 hours</option>
                <option value={720}>12 hours</option>
                <option value={1440}>24 hours</option>
              </select>
            </div>
            {saving && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Email alerts */}
            <div className="space-y-2 p-3 rounded-lg border bg-gray-50/50" data-testid="email-alert-section">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                  <Mail className="w-3.5 h-3.5" /> Email Alerts
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); saveConfig({ email_enabled: !config.email_enabled }); }}
                  className={`relative w-8 h-4 rounded-full transition-colors ${config.email_enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  data-testid="email-alert-toggle"
                >
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${config.email_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.email_enabled && (
                <>
                  <div className="space-y-1">
                    {(config.email_recipients || []).map(email => (
                      <div key={email} className="flex items-center justify-between bg-white rounded px-2 py-1 text-xs" data-testid={`email-recipient-${email}`}>
                        <span className="text-gray-700 truncate">{email}</span>
                        <button onClick={() => removeEmail(email)} className="text-gray-400 hover:text-red-500 ml-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addEmail()}
                      placeholder="Add email..."
                      className="h-7 text-xs"
                      data-testid="add-email-input"
                    />
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={addEmail} data-testid="add-email-btn">
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-gray-500" onClick={() => sendTest('email')} disabled={testing === 'email' || !(config.email_recipients || []).length} data-testid="test-email-btn">
                    {testing === 'email' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Send Test Email
                  </Button>
                </>
              )}
            </div>

            {/* Slack alerts */}
            <div className="space-y-2 p-3 rounded-lg border bg-gray-50/50" data-testid="slack-alert-section">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-xs font-medium text-gray-700">
                  <MessageSquare className="w-3.5 h-3.5" /> Slack Alerts
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); saveConfig({ slack_enabled: !config.slack_enabled }); }}
                  className={`relative w-8 h-4 rounded-full transition-colors ${config.slack_enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  data-testid="slack-alert-toggle"
                >
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${config.slack_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {config.slack_enabled && (
                <>
                  <Input
                    type="url"
                    value={config.slack_webhook_url || ''}
                    onChange={e => setConfig(prev => ({ ...prev, slack_webhook_url: e.target.value }))}
                    placeholder="https://hooks.slack.com/services/..."
                    className="h-7 text-xs"
                    data-testid="slack-webhook-input"
                  />
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => saveConfig({ slack_webhook_url: config.slack_webhook_url })} data-testid="save-slack-btn">
                      <Save className="w-3 h-3 mr-1" /> Save URL
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-gray-500" onClick={() => sendTest('slack')} disabled={testing === 'slack' || !config.slack_webhook_url} data-testid="test-slack-btn">
                      {testing === 'slack' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Test
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function SyncDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [triggeringSyncNow, setTriggeringSyncNow] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [syncHistory, setSyncHistory] = useState(null);
  const intervalRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [devRes, histRes] = await Promise.all([
        api.get('/sync-dashboard/devices'),
        api.get('/settings/ninjarmm-autosync/history?limit=20'),
      ]);
      setData(devRes.data);
      setSyncHistory(histRes.data);
    } catch {
      toast.error('Failed to load sync dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [loadData]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const isRunning = data?.autosync?.currently_running;
    const interval = isRunning ? 5000 : 30000;
    intervalRef.current = setInterval(loadData, interval);
    return () => clearInterval(intervalRef.current);
  }, [data?.autosync?.currently_running, loadData]);

  const handleRunNow = async () => {
    setTriggeringSyncNow(true);
    try {
      await api.post('/settings/ninjarmm-autosync/run-now');
      toast.success('Sync started');
      setTimeout(loadData, 2000);
    } catch {
      toast.error('Failed to trigger sync');
    } finally {
      setTriggeringSyncNow(false);
    }
  };

  const handleResetStuckSync = async () => {
    if (!window.confirm('Reset the stuck sync status? This will clear the running flag.')) return;
    try {
      await api.post('/settings/ninjarmm-autosync/reset');
      toast.success('Sync status reset');
      loadData();
    } catch {
      toast.error('Failed to reset sync');
    }
  };

  const handleResetHealth = async (assetId, assetName) => {
    setResettingId(assetId);
    try {
      await api.post(`/assets/${assetId}/sync-health/reset`);
      toast.success(`Reset sync health for ${assetName}`);
      loadData();
    } catch {
      toast.error('Failed to reset');
    } finally {
      setResettingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="sync-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a5490]" />
      </div>
    );
  }

  const { devices = [], summary = {}, autosync = {} } = data || {};
  const isRunning = autosync.currently_running;

  const filtered = devices.filter(d => {
    if (filter !== 'all' && d.dashboard_status !== filter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (d.name || '').toLowerCase().includes(q) || (d.customer_name || '').toLowerCase().includes(q) || (d.ninjarmm_device_id || '').toString().includes(q);
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal;
    if (sortBy === 'name') { aVal = (a.name || '').toLowerCase(); bVal = (b.name || '').toLowerCase(); }
    else if (sortBy === 'customer') { aVal = (a.customer_name || '').toLowerCase(); bVal = (b.customer_name || '').toLowerCase(); }
    else if (sortBy === 'last_sync') { aVal = a.seconds_since_sync ?? 999999; bVal = b.seconds_since_sync ?? 999999; }
    else if (sortBy === 'failures') { aVal = (a.sync_health?.consecutive_failures || 0); bVal = (b.sync_health?.consecutive_failures || 0); }
    else if (sortBy === 'status') { aVal = a.dashboard_status; bVal = b.dashboard_status; }
    else { aVal = a.name; bVal = b.name; }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const toggleSort = (col) => {
    if (sortBy === col) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortOrder('asc'); }
  };

  const summaryCards = [
    { key: 'total', label: 'Total Devices', value: summary.total || 0, icon: Server, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
    { key: 'healthy', label: 'Healthy', value: summary.healthy || 0, icon: Heart, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    { key: 'failing', label: 'Failing', value: summary.failing || 0, icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
    { key: 'auto_excluded', label: 'Auto-Excluded', value: summary.auto_excluded || 0, icon: ShieldOff, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
    { key: 'manually_excluded', label: 'Excluded', value: summary.manually_excluded || 0, icon: Ban, color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' },
    { key: 'never_synced', label: 'Never Synced', value: summary.never_synced || 0, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  ];

  return (
    <div className="space-y-6" data-testid="sync-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ninjarmm')} data-testid="back-to-ninjarmm-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Radio className="w-6 h-6 text-[#1a5490]" />
              Sync Dashboard
              {isRunning && <span className="ml-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600"><span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />LIVE</span>}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Real-time NinjaRMM device sync monitoring</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-dashboard-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button size="sm" onClick={handleRunNow} disabled={isRunning || triggeringSyncNow} data-testid="run-sync-now-btn" className="bg-[#1a5490] hover:bg-[#0f3a5f] text-white">
            {triggeringSyncNow ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Play className="w-4 h-4 mr-1.5" />}
            {isRunning ? 'Sync Running...' : 'Run Sync Now'}
          </Button>
        </div>
      </div>

      {/* Live Sync Progress */}
      {isRunning && (
        <Card className="border-blue-200 bg-blue-50" data-testid="live-sync-progress">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="text-sm font-medium text-blue-700">{autosync.progress || 'Syncing...'}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetStuckSync}
                className="text-amber-600 hover:text-amber-700 border-amber-300 hover:border-amber-400 h-7 text-xs"
                data-testid="reset-stuck-sync-btn"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Reset Stuck
              </Button>
            </div>
            <Progress value={autosync.assets_synced && autosync.assets_synced + autosync.assets_failed > 0 ? Math.min(100, ((autosync.assets_synced + autosync.assets_failed) / (summary.total || 1)) * 100) : undefined} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-blue-600">
              <span>{autosync.assets_synced || 0} synced, {autosync.assets_failed || 0} failed</span>
              <span>{summary.total || 0} total devices</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auto-sync status banner */}
      <div className={`flex items-center justify-between rounded-lg px-4 py-2.5 text-sm border ${autosync.enabled ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`} data-testid="autosync-status-banner">
        <div className="flex items-center gap-2">
          {autosync.enabled ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
          <span className="font-medium">Auto-Sync: {autosync.enabled ? 'ON' : 'OFF'}</span>
          {autosync.enabled && <span className="text-xs opacity-70">every {autosync.interval_minutes}min</span>}
        </div>
        {autosync.last_run && (
          <span className="text-xs opacity-70">Last run: {formatInCST ? formatInCST(autosync.last_run) : new Date(autosync.last_run).toLocaleString()}</span>
        )}
      </div>

      {/* Sync Performance Card */}
      {syncHistory && syncHistory.history.length > 0 && (
        <SyncPerformanceCard history={syncHistory.history} stats={syncHistory.stats} />
      )}

      {/* Sync Alert Settings */}
      <SyncAlertSettings />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-cards">
        {summaryCards.map(c => {
          const Icon = c.icon;
          const isActive = filter === c.key || (filter === 'all' && c.key === 'total');
          return (
            <Card
              key={c.key}
              className={`cursor-pointer transition-all hover:shadow-md ${c.bg} ${c.border} ${isActive ? 'ring-2 ring-[#1a5490] shadow-md' : ''}`}
              onClick={() => { setFilter(c.key === 'total' ? 'all' : c.key); setPage(0); }}
              data-testid={`filter-${c.key}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <Icon className={`w-4 h-4 ${c.color}`} />
                  {isActive && <div className="w-1.5 h-1.5 rounded-full bg-[#1a5490]" />}
                </div>
                <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{c.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Device Table */}
      <Card data-testid="device-table-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <CardTitle className="text-lg text-gray-900">
              Devices {filter !== 'all' && <Badge variant="secondary" className="ml-2 text-xs">{STATUS_CONFIG[filter]?.label}</Badge>}
              <span className="ml-2 text-sm font-normal text-gray-500">({filtered.length})</span>
            </CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search devices or customers..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
                className="pl-9"
                data-testid="device-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">Status</TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('name')} data-testid="sort-name">
                    <span className="flex items-center gap-1">Device <ArrowUpDown className="w-3 h-3" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer hidden md:table-cell" onClick={() => toggleSort('customer')}>
                    <span className="flex items-center gap-1">Customer <ArrowUpDown className="w-3 h-3" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => toggleSort('last_sync')} data-testid="sort-last-sync">
                    <span className="flex items-center gap-1">Last Sync <ArrowUpDown className="w-3 h-3" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer hidden lg:table-cell" onClick={() => toggleSort('failures')}>
                    <span className="flex items-center gap-1">Failures <ArrowUpDown className="w-3 h-3" /></span>
                  </TableHead>
                  <TableHead className="hidden xl:table-cell">Override</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-400 py-12">
                      {searchTerm ? 'No devices match your search' : 'No devices found'}
                    </TableCell>
                  </TableRow>
                )}
                {paged.map(device => {
                  const cfg = STATUS_CONFIG[device.dashboard_status] || STATUS_CONFIG.unknown;
                  const health = device.sync_health || {};
                  const override = device.sync_override || {};
                  const failures = health.consecutive_failures || 0;

                  return (
                    <TableRow key={device.id} className="hover:bg-gray-50" data-testid={`device-row-${device.id}`}>
                      <TableCell>
                        <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} title={cfg.label} />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{device.name || 'Unnamed'}</div>
                          <div className="text-xs text-gray-400">ID: {device.ninjarmm_device_id}</div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="text-sm text-gray-600">{device.customer_name || '—'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-700">{formatTimeAgo(device.seconds_since_sync)}</div>
                        {device.ninjarmm_last_sync && (
                          <div className="text-[11px] text-gray-400">{formatInCST ? formatInCST(device.ninjarmm_last_sync) : new Date(device.ninjarmm_last_sync).toLocaleString()}</div>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {failures > 0 ? (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-xs">
                            {failures} consecutive
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        {override.mode === 'custom' && (
                          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 text-xs">Custom: {override.custom_interval_minutes}min</Badge>
                        )}
                        {override.mode === 'excluded' && !override.auto_excluded && (
                          <Badge variant="outline" className="text-xs">Manual</Badge>
                        )}
                        {override.auto_excluded && (
                          <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50 text-xs">Auto</Badge>
                        )}
                        {(!override.mode || override.mode === 'global') && (
                          <span className="text-xs text-gray-400">Global</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(device.dashboard_status === 'failing' || device.dashboard_status === 'auto_excluded') && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-7 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleResetHealth(device.id, device.name)}
                              disabled={resettingId === device.id}
                              data-testid={`reset-health-${device.id}`}
                            >
                              {resettingId === device.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                              Reset
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            className="h-7 px-2 text-xs text-gray-500 hover:text-gray-900"
                            onClick={() => navigate(`/assets/${device.id}`)}
                            data-testid={`view-asset-${device.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-xs text-gray-500">
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} of {sorted.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="h-7 px-2" data-testid="prev-page-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-gray-500 px-2">Page {page + 1} / {totalPages}</span>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="h-7 px-2" data-testid="next-page-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
